import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import {
  Box, FormField, LoadingBubbles, Tag, Text,
} from '@campaignhub/suit-theme'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faSearch } from '@fortawesome/pro-light-svg-icons'

import styles from './styles.module.scss'

const InfiniteLinkingTags = (props) => {
  const {
    selectedItems,
    onSelect,
    onRemove,
    onLoadMore,
    hasMore,
    loading,
    loadingLinkedItems,
    options,
    labelKey,
    subLabelKey,
    valueKey,
    disabled,
    label,
    secondLabel,
    errorMessage,
    fullWidth,
    itemsDirection,
    searchFilter,
    onSearchChange,
    placeholder,
    showEmptyText,
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const listRef = useRef(null)

  const selectedValues = useMemo(() => new Set(selectedItems.map(item => String(item[valueKey]))), [selectedItems, valueKey])
  const filteredOptions = options.filter(opt => !selectedValues.has(String(opt[valueKey])))

  const resolveKey = (obj, key) => {
    if (!obj || !key) return undefined
    return key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), obj)
  }

  const handleSelect = useCallback((opt) => {
    onSelect(opt)
    setIsOpen(false)
  }, [onSelect])

  const handleScroll = useCallback(() => {
    if (!listRef.current || loading || !hasMore) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      onLoadMore()
    }
  }, [loading, hasMore, onLoadMore])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Box flexDirection="column">
      <FormField
        boxProps={{ flex: 1, paddingBottom: ['large', 0], marginRight: [0, 'medium'] }}
        label={secondLabel ? `${label} ${secondLabel}` : label}
        errorMessage={errorMessage}
      >
        <Box flexDirection="row" width={fullWidth !== false ? '100%' : ['100%', 'calc(50% - 4px)']}>
          <div ref={containerRef} className={styles['dropdown-container']}>
            <div className={styles['search-input-wrapper']}>
              <FontAwesomeIcon icon={faSearch} className={styles['search-icon']} />
              <input
                className={styles.input}
                type="text"
                placeholder={placeholder || `Search ${label}`}
                value={searchFilter}
                disabled={disabled}
                onChange={(e) => {
                  if (onSearchChange) onSearchChange(e.target.value)
                  setIsOpen(true)
                }}
                onFocus={() => !disabled && setIsOpen(true)}
                onKeyDown={(e) => {
                  if (disabled) return
                  if (e.key === 'Escape') setIsOpen(false)
                  if (e.key === 'ArrowDown' && !isOpen) setIsOpen(true)
                }}
              />
            </div>

            {isOpen && (
              <ul
                ref={listRef}
                role="listbox"
                className={styles['dropdown-list']}
                onScroll={handleScroll}
              >
                {filteredOptions.length > 0 ? filteredOptions.map(opt => (
                  <li
                    key={String(opt[valueKey])}
                    role="option"
                    className={styles.option}
                    onClick={() => handleSelect(opt)}
                  >
                    {opt[labelKey]}
                    {subLabelKey && resolveKey(opt, subLabelKey) && (
                      <div className={styles['option-sub-label']}>
                        {resolveKey(opt, subLabelKey)}
                      </div>
                    )}
                  </li>
                )) : (
                  !loading && <li className={styles.status}>No results found</li>
                )}

                {loading && <li className={styles['status-loading']}>Loading...</li>}
              </ul>
            )}
          </div>
        </Box>

        <Box flexDirection={itemsDirection || 'row'} flexWrap="wrap" gridGap="small" marginTop="medium">
          {selectedItems.length > 0 && [...selectedItems]
            .sort((a, b) => {
              const labelA = a[labelKey] || '';
              const labelB = b[labelKey] || '';
              return labelA > labelB ? 1 : -1;
            })
            .map(item => (
              <Tag
                key={item[valueKey]}
                boxProps={{
                  width: 'fit-content',
                  fontSize: 'xsmall',
                  onClick: () => (disabled ? null : onRemove(item)),
                }}
              >
                <Text color="bodyFontLightColor" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
                  {item[labelKey]} &nbsp;<FontAwesomeIcon icon={faTimes} />
                </Text>
              </Tag>
            ))}
          {showEmptyText && !selectedItems.length && !loading && !loadingLinkedItems && label && (
            <Text color="bodyFontLightColor" fontSize="xsmall">
              No linked {label.toLowerCase()}
            </Text>
          )}
          {(loading || loadingLinkedItems) && !isOpen && <LoadingBubbles color="rgba(0, 0, 0, 0.15)" style={{ flexShrink: 0 }} />}
        </Box>
      </FormField>
    </Box>
  )
}

InfiniteLinkingTags.propTypes = {
  selectedItems: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  loading: PropTypes.bool,
  loadingLinkedItems: PropTypes.bool,
  options: PropTypes.array,
  labelKey: PropTypes.string,
  subLabelKey: PropTypes.string,
  valueKey: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  secondLabel: PropTypes.string,
  errorMessage: PropTypes.string,
  fullWidth: PropTypes.bool,
  itemsDirection: PropTypes.string,
  searchFilter: PropTypes.string,
  onSearchChange: PropTypes.func,
  placeholder: PropTypes.string,
  showEmptyText: PropTypes.bool,
}

InfiniteLinkingTags.defaultProps = {
  selectedItems: [],
  hasMore: false,
  loading: false,
  loadingLinkedItems: false,
  options: [],
  labelKey: 'name',
  valueKey: 'id',
  disabled: false,
  label: '',
  secondLabel: '',
  searchFilter: '',
  fullWidth: true,
  showEmptyText: true,
}

export default InfiniteLinkingTags
